import {
  AdviceError,
  InsuranceCoverage,
  PaymentReason,
  UserAuthorizationLevel,
} from "Store/advice/interfaces";

import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";

export interface AdviceResponse {
  readonly payload: CollectiveAdvice;
  readonly errors?: AdviceError[];
}

export interface CollectiveAdvice {
  readonly authorizationLevel: UserAuthorizationLevel;
  readonly needs: Record<NeedName, CoverageNeed>;
  readonly additionalInformation: AdditionalInformation;
}

export enum NeedName {
  LIFE = "LIFE",
  DISABILITY = "DISABILITY",
  CRITICAL_ILLNESS = "CRITICAL_ILLNESS",
}

export interface CoverageNeed {
  readonly need: number;
  readonly needReasons: Partial<Record<PaymentReason, number>>;
  readonly existingCoverages: Record<ExistingCoverageName, number>;
  readonly recommendedCoverage?: InsuranceCoverage;
  readonly advice: Partial<Record<ProductName, InsuranceAdvice>>;
  readonly warnings: CoverageWarning[];
}

interface AdditionalInformation {
  readonly baseAmount: number;
  readonly netIncome: number;
}

enum ExistingCoverageName {
  NAV_PAYOUT = "NAV_PAYOUT",
  EMPLOYER_PAYOUT = "EMPLOYER_PAYOUT",
  SICKNESS_PAYOUT = "SICKNESS_PAYOUT",
}

export type ProductName =
  | INSURANCE_NAME_VALUE.AKA_GROUP_LIFE
  | INSURANCE_NAME_VALUE.AKA_CRITICAL_ILLNESS
  | INSURANCE_NAME_VALUE.DEATH
  | INSURANCE_NAME_VALUE.DISABILITY_FLEX
  | INSURANCE_NAME_VALUE.CRITICAL_ILLNESS;

export interface InsuranceAdvice {
  readonly payments: Partial<Record<ProductCoverageName, PaymentConfiguration>>;
}

export enum ProductCoverageName {
  AKA_CRITICAL_ILLNESS = "AKA_CRITICAL_ILLNESS",
  AKA_CRITICAL_ILLNESS_CO_INSURED = "AKA_CRITICAL_ILLNESS_CO_INSURED",
  AKA_GROUP_LIFE_INSURED_DEATH = "AKA_GROUP_LIFE_INSURED_DEATH",
  AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK = "AKA_GROUP_LIFE_INSURED_INCAPACITY_OF_WORK",
  AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST = "AKA_GROUP_LIFE_INSURED_TIME_LIMITED_DISABILITY_INTEREST",
  AKA_GROUP_LIFE_CO_INSURED_DEATH = "AKA_GROUP_LIFE_CO_INSURED_DEATH",
  AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK = "AKA_GROUP_LIFE_CO_INSURED_INCAPACITY_OF_WORK",
  AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST = "AKA_GROUP_LIFE_CO_INSURED_TIME_LIMITED_DISABILITY_INTEREST",
  CRITICAL_ILLNESS = "CRITICAL_ILLNESS",
  DEATH = "DEATH",
  DISABILITY_FLEX_CAPITAL = "DISABILITY_FLEX_CAPITAL",
  DISABILITY_FLEX_PENSION = "DISABILITY_FLEX_PENSION",
}

export interface PaymentConfiguration {
  readonly restrictions: PaymentRestrictionConfiguration;
  readonly advice: PaymentAdvice;
  readonly effectivePaymentRate: number;
  readonly warnings: PaymentWarning[];
}

interface PaymentRestrictionConfiguration {
  readonly paymentRestriction: PaymentRestriction;
  readonly purchaseRestriction: PurchaseRestriction;
  readonly restrictionReasons: PurchaseRestrictionReason[];
  readonly additionalRestrictions: AdditionalRestriction[];
}

export interface PaymentRestriction {
  readonly minPayouts: Record<UserAuthorizationLevel, number>;
  readonly maxPayouts: Record<UserAuthorizationLevel, number>;
  readonly validStep: number | null;
  readonly validSteps: number[] | null;
}

export enum PurchaseRestriction {
  ALLOWED = "ALLOWED",
  NOT_ALLOWED = "NOT_ALLOWED",
  NOT_ONLINE = "NOT_ONLINE", // Student
}

export enum PurchaseRestrictionReason {
  AGE = "AGE",
  STUDENT = "STUDENT",
  WORK_SITUATION = "WORK_SITUATION",
  INCOME = "INCOME",
  RESIDENCY = "RESIDENCY",
}

export enum AdditionalRestriction {
  ELIGIBLE_FOR_PAYMENT_AFTER_3_MONTHS = "ELIGIBLE_FOR_PAYMENT_AFTER_3_MONTHS",
}

interface PaymentAdvice {
  readonly recommended: number;
  readonly minValid: number;
  readonly maxValid: number;
}

enum CoverageWarning {
  EMPLOYER_DISABILITY_PENSION_UNKNOWN = "EMPLOYER_DISABILITY_PENSION_UNKNOWN",
}

export enum PaymentWarning {
  CLOSE_TO_MAX_AGE = "CLOSE_TO_MAX_AGE",
}
